import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

export default function LaundryPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL || ''}/api/laundry/public/${token}`)
      .then(r => setData(r.data))
      .catch(() => setError('Invalid or expired link.'));
  }, [token]);

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-500 font-medium">{error}</p>
        <p className="text-gray-400 text-sm mt-1">This link may be invalid or no longer active.</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-gray-400">Loading...</p>
    </div>
  );

  const { student, entries, totalCharged, totalPaid, balance, bankDetails } = data;
  const hasBankDetails = bankDetails && (bankDetails.bankName || bankDetails.bankAccountNumber);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-brand-700">Eden Crest Girls Hostel</h1>
          <p className="text-sm text-gray-500">Laundry Statement</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
          <p className="font-semibold text-gray-800">{student.name}</p>
          <p className="text-xs text-brand-600 font-mono">{student.registrationNo}</p>
        </div>

        {hasBankDetails && (
          <div className="bg-white rounded-xl border border-blue-100 shadow-sm p-4 mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">Payment Details</p>
            <div className="space-y-1.5">
              {bankDetails.bankName && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Bank</span>
                  <span className="font-medium text-gray-700">{bankDetails.bankName}</span>
                </div>
              )}
              {bankDetails.bankAccountName && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Account Name</span>
                  <span className="font-medium text-gray-700">{bankDetails.bankAccountName}</span>
                </div>
              )}
              {bankDetails.bankAccountNumber && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Account No.</span>
                  <span className="font-medium text-gray-700 font-mono">{bankDetails.bankAccountNumber}</span>
                </div>
              )}
              {bankDetails.bankBranch && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Branch</span>
                  <span className="font-medium text-gray-700">
                    {bankDetails.bankBranch}{bankDetails.bankBranchCode ? ` (${bankDetails.bankBranchCode})` : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-red-50 rounded-xl p-3 text-center">
            <p className="text-xs text-red-500 font-medium">Charged</p>
            <p className="text-base font-bold text-red-700">Rs. {totalCharged.toFixed(2)}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center">
            <p className="text-xs text-green-500 font-medium">Paid</p>
            <p className="text-base font-bold text-green-700">Rs. {totalPaid.toFixed(2)}</p>
          </div>
          <div className={`rounded-xl p-3 text-center ${balance > 0 ? 'bg-orange-50' : 'bg-blue-50'}`}>
            <p className={`text-xs font-medium ${balance > 0 ? 'text-orange-500' : 'text-blue-500'}`}>Balance</p>
            <p className={`text-base font-bold ${balance > 0 ? 'text-orange-700' : 'text-blue-700'}`}>
              Rs. {Math.abs(balance).toFixed(2)}
              {balance < 0 && <span className="text-xs font-normal ml-1">credit</span>}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50">
            <p className="text-sm font-semibold text-gray-700">Recent Activity <span className="text-xs text-gray-400 font-normal">(last 10)</span></p>
          </div>
          {entries.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-8">No records found</p>
          )}
          <div className="divide-y divide-gray-50">
            {entries.map(e => (
              <div key={e.id} className="flex items-center gap-3 px-4 py-3">
                <div className={`w-1 self-stretch rounded-full ${e.type === 'CHARGE' ? 'bg-red-400' : 'bg-green-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 truncate">
                    {e.description || (e.type === 'PAYMENT' ? 'Payment received' : 'Laundry charge')}
                  </p>
                  <p className="text-xs text-gray-400">{new Date(e.date).toLocaleDateString()}</p>
                </div>
                <p className={`text-sm font-semibold ${e.type === 'CHARGE' ? 'text-red-600' : 'text-green-600'}`}>
                  {e.type === 'CHARGE' ? '-' : '+'}Rs. {e.amount.toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {balance > 0 && (
          <div className="mt-4 bg-orange-50 border border-orange-200 rounded-xl p-3 text-center">
            <p className="text-orange-700 font-semibold text-sm">Outstanding Balance: Rs. {balance.toFixed(2)}</p>
            <p className="text-orange-500 text-xs mt-0.5">Please settle at your earliest convenience</p>
          </div>
        )}
        {balance <= 0 && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-3 text-center">
            <p className="text-green-700 font-semibold text-sm">No outstanding balance</p>
            {balance < 0 && <p className="text-green-500 text-xs mt-0.5">Credit of Rs. {Math.abs(balance).toFixed(2)} available</p>}
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          Generated by Eden Crest Management System
        </p>
      </div>
    </div>
  );
}
